import type { TFunction } from "i18next";
import { toast } from "@/hooks/use-toast";
import { isConversationUnavailableError } from "@/lib/api-error";

/**
 * Surface the "this booking's conversation is unavailable" 404 (task #5759)
 * as a clear, contextual toast.
 *
 * POST /messages/conversations/find-or-create returns a 404 for trips whose
 * participants (car_id / renter_id / owner_id) are NULL — legacy imported
 * bookings that genuinely cannot be messaged. Trip-aware "Message host /
 * guest" CTAs used to fall through to a generic "couldn't open chat" toast
 * (or, on the confirmation page, silently swallow the error), which told the
 * user nothing about *why*. This helper detects that specific 404 and shows a
 * plain-language explanation instead.
 *
 * Use it as the first line of a `catch` block (after the rate-limit check
 * where one is present):
 *
 *   } catch (err) {
 *     if (showRateLimitToastIfNeeded(err, t)) return;
 *     if (showConversationUnavailableToastIfNeeded(err, t)) return;
 *     // …existing generic handling…
 *   }
 *
 * It shows the toast and returns `true` only for the conversation-unavailable
 * 404, leaving every other error for the caller's existing handling (returns
 * `false`).
 */
export function showConversationUnavailableToastIfNeeded(
  error: unknown,
  t: TFunction,
): boolean {
  if (!isConversationUnavailableError(error)) return false;
  toast({
    title: t("trip.conversation_unavailable_title", "Conversation unavailable"),
    description: t(
      "trip.conversation_unavailable_desc",
      "This booking's conversation isn't available to message. This can happen with older imported bookings. Please contact support if you need help.",
    ),
    variant: "destructive",
  });
  return true;
}
